/**
 * The reviewer's workspace.
 *
 * Two panes: the paper as pupils will see it, and the same paper with the answers
 * marked. Reviewing without the answer key means checking arithmetic by hand;
 * having to open a second document to get it means losing your place.
 *
 * Comments attach to a question. A reviewer used to have one box for the whole
 * paper, so "question 3 has two correct answers and question 7 is missing its
 * marks" arrived as prose the author had to map back onto their own document.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, saveBlob } from '../../lib/api/client'
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  PageHeader,
  Pill,
  Spinner,
  Textarea,
  Toolbar,
  Select,
} from '../../components/ui'
import { Icon } from '../../components/icons'
import { STATUS_TONE } from '../../components/statusTone'
import { toast } from '../../components/toast'
import { Preview } from '../preview/Preview'
import { when } from './format'
import './queues.css'

/**
 * The open paper is a route, not a piece of local state.
 *
 * It used to be `useState`, which meant the address bar still said `/review`
 * while a paper filled the screen: Back left the queue entirely instead of
 * closing the paper, a refresh lost your place, and a reviewer could not send
 * anyone a link to the thing they were looking at. `/papers/:id` already worked
 * this way; this is the same shape.
 */
export function Review() {
  const { id: openId } = useParams()
  const navigate = useNavigate()
  const queue = useQuery({ queryKey: ['queue', 'review'], queryFn: api.reviewQueue })

  if (openId) return <ReviewPaper worksheetId={openId} onClose={() => navigate('/review')} />

  if (queue.isLoading) return <Spinner label="Loading the review queue" />

  const rows = queue.data ?? []
  const setOpenId = (id: string) => navigate(`/review/${id}`)

  return (
    <div className="tc-page tc-review-queue">
      <PageHeader
        eyebrow="Review workspace"
        title="Review"
        icon={<Icon.review size={20} />}
        description="Check papers colleagues have submitted and return clear comments or approval."
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={<Icon.review size={20} />}
          title="Nothing to review"
          message="When a colleague sends you a paper it appears here, ready to check and comment on."
        />
      ) : (
        <div className="tc-table-wrap">
        <table className="tc-table tc-review-table">
          <thead>
            <tr>
              <th>Paper</th>
              <th style={{ width: 160 }}>From</th>
              <th style={{ width: 90 }}>Questions</th>
              <th style={{ width: 110 }}>Comments</th>
              <th style={{ width: 150 }}>Sent</th>
              <th style={{ width: 100 }}>
                <span className="tc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td data-label="Paper">
                  <button className="tc-linkish" onClick={() => setOpenId(row.id)}>
                    {row.title}
                  </button>
                </td>
                <td data-label="From">{row.author}</td>
                <td data-label="Questions">{row.question_count}</td>
                <td data-label="Comments">{row.open_comments > 0 ? <Pill tone="warning">{row.open_comments} open</Pill> : '—'}</td>
                <td data-label="Sent" className="tc-cell-muted">{when(row.submitted_at)}</td>
                <td data-label="Actions">
                  <Button size="sm" onClick={() => setOpenId(row.id)}>
                    Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  )
}

function ReviewPaper({ worksheetId, onClose }: { worksheetId: string; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState<'approve' | 'request_changes' | null>(null)
  const [note, setNote] = useState('')
  const [commentOn, setCommentOn] = useState<string | null>(null)
  const [commentText, setCommentText] = useState('')

  const paper = useQuery({ queryKey: ['worksheet', worksheetId], queryFn: () => api.getWorksheet(worksheetId) })
  const answersAllowed = paper.data?.answers_allowed ?? false
  const pupil = useQuery({ queryKey: ['preview', worksheetId, false], queryFn: () => api.preview(worksheetId, false) })
  const marked = useQuery({
    queryKey: ['preview', worksheetId, true],
    queryFn: () => api.preview(worksheetId, true),
    enabled: answersAllowed,
  })
  const comments = useQuery({ queryKey: ['comments', worksheetId], queryFn: () => api.comments(worksheetId) })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['comments', worksheetId] })
    queryClient.invalidateQueries({ queryKey: ['queue', 'review'] })
  }

  const addComment = useMutation({
    mutationFn: () => api.addComment(worksheetId, commentText, commentOn),
    onSuccess: () => {
      setCommentText('')
      setCommentOn(null)
      refresh()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const decide = useMutation({
    mutationFn: () =>
      api.perform(worksheetId, { action: decision!, comment: note || null, reviewer_id: null }),
    onSuccess: () => {
      setDecision(null)
      refresh()
      onClose()
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (paper.isLoading) return <Spinner label="Opening the paper" />

  const questions = paper.data?.questions ?? []
  const openComments = (comments.data ?? []).filter((comment) => !comment.resolved)

  return (
    <div className="tc-review">
      <Toolbar>
        <Button size="sm" icon={<Icon.back size={15} />} onClick={onClose}>
          Back to queue
        </Button>
        <h1 className="tc-queue-paper-title">{paper.data?.title}</h1>
        <Pill tone={STATUS_TONE[paper.data?.status ?? ''] ?? 'muted'}>
          {paper.data?.status_label}
        </Pill>
        <div className="tc-spacer" />
        {/* The reason is on screen, not only in the tooltip: a disabled button
            cannot take focus, so `title` alone reaches nobody using a keyboard
            or a screen reader. Same treatment as the builder and My papers. */}
        <span className="tc-review-export">
          <Button
            size="sm"
            icon={<Icon.download size={15} />}
            disabled={!answersAllowed}
            title={answersAllowed ? 'Download with answers' : 'Teacher copy is disabled for exam sheets'}
            onClick={() =>
              api
                .exportDocx(worksheetId, true)
                .then((blob) => saveBlob(blob, 'teacher_copy.docx'))
                .catch((error: Error) => toast(error.message, 'error'))
            }
          >
            Teacher copy
          </Button>
          {!answersAllowed && <span className="tc-disabled-action-note">Not for exams</span>}
        </span>
        <Button size="sm" onClick={() => setDecision('request_changes')}>
          Request changes
        </Button>
        <Button size="sm" variant="primary" onClick={() => setDecision('approve')}>
          Approve
        </Button>
      </Toolbar>

      <div className="tc-review-panes">
        <section aria-label="As pupils will see it">
          <h2 className="tc-pane-title">As pupils will see it</h2>
          <Preview data={pupil.data} loading={pupil.isLoading} error={pupil.isError} />
        </section>

        <section aria-label={answersAllowed ? 'With answers' : 'Answer view unavailable'}>
          <h2 className="tc-pane-title">
            {answersAllowed ? 'With answers' : 'Answer view unavailable'}
          </h2>
          {answersAllowed ? (
            <Preview data={marked.data} loading={marked.isLoading} error={marked.isError} />
          ) : (
            <div className="tc-answer-disabled" role="note">
              <Icon.warning size={18} />
              <strong>Answers are hidden for exam sheets.</strong>
              <span>Review the pupil copy and leave comments against each question.</span>
            </div>
          )}
        </section>

        <section className="tc-review-notes" aria-label="Comments">
          <h2 className="tc-pane-title">
            Comments {openComments.length > 0 && <Pill tone="warning">{openComments.length} open</Pill>}
          </h2>

          <div className="tc-comment-add">
            <Select
              aria-label="Comment target"
              value={commentOn ?? ''}
              onChange={(event) => setCommentOn(event.target.value || null)}
            >
              <option value="">About the whole paper</option>
              {questions.map((question, index) => (
                <option key={question.id} value={question.id}>
                  Question {index + 1}
                </option>
              ))}
            </Select>
            <Textarea
              aria-label="Comment"
              rows={2}
              value={commentText}
              placeholder="Question 3 has two correct answers."
              onChange={(event) => setCommentText(event.target.value)}
            />
            <Button
              size="sm"
              variant="primary"
              disabled={!commentText.trim() || addComment.isPending}
              onClick={() => addComment.mutate()}
            >
              Add comment
            </Button>
          </div>

          <ul className="tc-comments">
            {(comments.data ?? []).map((comment) => {
              const position = questions.findIndex((question) => question.id === comment.question_id)
              return (
                <li key={comment.id} className={comment.resolved ? 'is-resolved' : ''}>
                  <span className="tc-comment-where">
                    {comment.question_id && position >= 0 ? `Q${position + 1}` : 'Paper'}
                  </span>
                  <span className="tc-comment-body">{comment.body}</span>
                  <span className="tc-comment-who">{comment.author}</span>
                </li>
              )
            })}
            {(comments.data ?? []).length === 0 && (
              <li className="tc-comments-empty tc-field-hint">No comments yet.</li>
            )}
          </ul>
        </section>
      </div>

      {/* The dialog states where the paper goes and what happens, before the
          confirming click - not after. */}
      <Dialog
        open={decision !== null}
        title={decision === 'approve' ? 'Approve this paper?' : 'Ask for changes?'}
        onClose={() => setDecision(null)}
        footer={
          <>
            <Button onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={decide.isPending || (decision === 'request_changes' && !note.trim())}
              onClick={() => decide.mutate()}
            >
              {decision === 'approve' ? 'Approve' : 'Send back'}
            </Button>
          </>
        }
      >
        <p className="tc-dialog-lead">
          {decision === 'approve'
            ? 'It goes to the printing team, and the file they receive is frozen as it is now. The teacher cannot edit it while it is there.'
            : 'It goes back to the teacher, who can edit it again. Say what needs changing so they know what to do.'}
        </p>
        {decision === 'request_changes' && (
          <Field
            label="What needs changing?"
            hint="Required. Any comments you left on questions go back with it."
          >
            <Textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
          </Field>
        )}
      </Dialog>
    </div>
  )
}
